import { useState } from 'react';
import { apiFetch, ApiError } from '../../services/api-client';

// "Add to Google Wallet" under the Pixel Pass — the passive tier's Android
// entry point. The pass mirrors this shop card (lifetime stamps + member QR)
// and updates by itself on every approved stamp. Hidden on iOS only (that
// side gets the Apple Wallet pass): Android is the target, and desktop works
// too — Google's save page adds the pass to the signed-in Google account
// (viewable at wallet.google.com), which is also how we test without an
// Android device. Renders nothing until the server is configured (501 →
// hide quietly).

interface Props {
  shopId?: string; // Supabase shop uuid
}

export default function AddToGoogleWallet({ shopId }: Props) {
  const [busy, setBusy] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
  if (!shopId || isIOS || hidden) return null;

  async function add() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const { url } = await apiFetch<{ url: string }>('/api/account', {
        action: 'wallet-save-link',
        shopId,
      });
      window.location.href = url; // Google's save page; back-button returns
    } catch (e) {
      if (e instanceof ApiError && e.status === 501) {
        setHidden(true); // wallet not configured yet — feature is dark
      } else {
        setError('Wallet link failed — try again');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col items-center gap-1">
      <button
        onClick={add}
        disabled={busy}
        className="w-full rounded-full bg-tab-inactive text-body-grey font-grotesk font-bold text-[13px] py-3 mt-2 transition-colors hover:text-primary disabled:opacity-50"
      >
        {busy ? 'Opening wallet…' : 'Add card to Google Wallet'}
      </button>
      {error && <p className="font-grotesk font-medium text-[12px] text-error">{error}</p>}
    </div>
  );
}
